(() => {
  if (window.SkinsColorExperiment) return;
  window.SkinsColorExperiment = true;
  const flagKey = 'skins-kennenlernen-color-mode';
  let unsubscribe;
  let currentHero;
  let defaultMode;
  let exposedVariant;
  let assignedVariant;
  const diagnosticSteps = new Set();
  let phase = 'initialize';
  const diagnose = (step, reason = '') => {
    if (window.MarmeladeAnalyticsConsent !== true) return;
    const key = `${step}:${reason}`;
    if (diagnosticSteps.has(key)) return;
    const hero = document.querySelector('meta-ads-hero[data-skins-color-mode]');
    if (!hero) return;
    try {
      window.posthog.capture('skins experiment diagnostic', {
        diagnostic_version: '2026-09-13.2', experiment_key: flagKey,
        step, reason, phase, source: 'shopify_theme',
        pathname: window.location.pathname,
        theme_id: window.Shopify?.theme?.id,
        theme_role: window.Shopify?.theme?.role,
        experiment_enabled: hero.dataset.skinsExperimentEnabled === 'true',
        color_mode: hero.dataset.skinsColorMode,
        applied_variant: hero.dataset.skinsExperimentVariant || 'none',
        exposure_attempted: Boolean(exposedVariant)
      });
      diagnosticSteps.add(key);
    } catch (error) {
      // Diagnostic failures must not interrupt the existing experiment.
      console.error('[Skins experiment] Diagnostic capture failed.', error);
    }
  };
  const checked = (step, action) => {
    phase = step;
    try { return action(); }
    catch (error) {
      // Never transmit arbitrary error messages/URLs or suppress the original failure.
      diagnose('error', step);
      console.error(`[Skins experiment] Failed during ${step}.`, error);
      throw error;
    }
  };

  const stop = () => {
    if (typeof unsubscribe === 'function') unsubscribe();
    unsubscribe = undefined;
    if (currentHero) {
      currentHero.dataset.skinsColorMode = defaultMode;
      delete currentHero.dataset.skinsExperimentVariant;
      window.MetaAdsLanding.renderGrids();
    }
    currentHero = undefined;
  };

  const initialize = () => {
    checked('reset', stop);
    const hero = document.querySelector('meta-ads-hero[data-skins-color-mode]');
    if (!hero) return;
    diagnose('initialize');
    if (hero.dataset.skinsExperimentEnabled !== 'true') { diagnose('excluded', 'switch_off'); return; }
    if (window.Shopify?.designMode || window.Shopify?.theme?.role !== 'main') { diagnose('excluded', 'preview'); return; }
    if (window.MarmeladeAnalyticsConsent !== true) return;
    const posthog = window.posthog;
    if (typeof posthog?.onFeatureFlags !== 'function' || typeof posthog?.capture !== 'function') {
      diagnose('error', 'sdk_unavailable');
      throw new Error('[Skins experiment] PostHog is unavailable.');
    }
    currentHero = hero;
    defaultMode = hero.dataset.skinsColorMode;
    unsubscribe = checked('subscribe', () => posthog.onFeatureFlags((_flags, variants, context) => {
      if (currentHero !== hero || window.MarmeladeAnalyticsConsent !== true) return;
      diagnose('flags_received');
      if (context?.errorsLoading) {
        diagnose('error', 'flags_loading');
        console.error('[Skins experiment] Feature flags could not be loaded; no exposure recorded.');
        return;
      }
      const variant = variants[flagKey];
      if (variant === false || variant === undefined) {
        diagnose('excluded', variant === false ? 'flag_inactive' : 'flag_missing');
        hero.dataset.skinsColorMode = defaultMode;
        delete hero.dataset.skinsExperimentVariant;
        checked('restore_render', () => window.MetaAdsLanding.renderGrids());
        return;
      }
      if (variant !== 'control' && variant !== 'test') {
        diagnose('error', 'unexpected_variant');
        console.error('[Skins experiment] Unexpected variant:', variant);
        return;
      }
      if (assignedVariant && assignedVariant !== variant) {
        diagnose('error', 'variant_changed');
        console.error('[Skins experiment] Variant changed during this page visit; reload required.');
        return;
      }
      hero.dataset.skinsColorMode = variant === 'test' ? 'dark' : 'light';
      hero.dataset.skinsExperimentVariant = variant;
      checked('render', () => window.MetaAdsLanding.renderGrids());
      assignedVariant = variant;
      diagnose('render_complete', variant);
      if (exposedVariant) return;
      // onFeatureFlags may have been queued on the snippet array, which the SDK
      // replaces after initialization. Never capture through that stale closure.
      const activePosthog = window.posthog;
      if (activePosthog !== posthog) diagnose('sdk_reference', 'replaced');
      if (Array.isArray(activePosthog) || typeof activePosthog?.capture !== 'function') {
        diagnose('error', 'sdk_not_ready');
        console.error('[Skins experiment] SDK is not ready; exposure remains pending.');
        return;
      }
      diagnose('exposure_call', variant);
      const result = checked('exposure_capture', () => activePosthog.capture('$experiment_exposure', {
        $feature_flag: flagKey,
        $feature_flag_response: variant,
        ['$feature/' + flagKey]: variant,
        pathname: window.location.pathname,
        source: 'shopify_theme',
        color_mode: hero.dataset.skinsColorMode
      }));
      if (result != null) exposedVariant = variant;
      // A returned SDK call is not an ingestion/server acknowledgement.
      diagnose('exposure_returned', result == null ? 'no_result' : 'result');
      if (result == null) {
        console.error('[Skins experiment] SDK did not accept exposure; a later flag callback may retry.');
      }
    }));
    checked('reload_flags', () => posthog.reloadFeatureFlags());
  };

  window.addEventListener('marmelade:analytics-consent', initialize);
  document.addEventListener('shopify:section:load', initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
